/*
  PLUSH + OAK — BUILDER SUMMARY, DELIVERY, PAYMENT, ADD TO CART
  --------------------------------------------------------------------------
  The configurator put everything in one taupe card and then fired Add to Cart
  before delivery had been mentioned at all: build rows, total, bundle offer
  and the button stacked together, with the freight panel and the postcode
  check stranded BELOW the button where nobody reads them. Courtney,
  2026-09-19: "the builder section is looking a bit messy... too many things
  in the Taupe builder area."

  Crate & Barrel's product page is the reference. Theirs runs Selection
  Summary (its own bordered card) → In-Home Delivery (service name, a postal
  code shown as already-answered with an edit affordance, then what the fee
  includes) → price → Add to Cart. The postal code is never an empty prompt;
  it is a resolved value you may change.

  So the order here becomes:
      1  Your build        — the summary card, on its own
      2  Delivery          — the rate, with the destination editable
      3  Payment           — pay in full or the 50% production deposit
      4  Add to Cart

  THE DESTINATION IS ANSWERED BY DEFAULT. Unlimited Delivery is one flat rate
  across the whole contiguous US and one across every Canadian province, so
  the default is simply correct for all but two states and nothing needs to be
  asked. "Change destination" reveals the configurator's own estimator for the
  shopper who wants Alaska or Hawaii, and once that returns a quote the panel
  switches to the quoted figure and the claim list that actually holds there.

  ⚠ RATE_BY_MARKET is the site's only hardcoded price, and it is the DEFAULT
  state only — the moment the estimator returns anything, the number and the
  claims come from it instead. A product page has no cart, and
  /cart/shipping_rates.json prices a cart rather than a variant, so there is
  nothing live to read before the estimator runs. Courtney approved this:
  ".com just puts out the Unlimited Furniture Delivery rate for USD, and .ca
  does the same for CAD." The CAD figure drifts with FX and is re-tuned
  quarterly — re-tune this constant with it.

  Loaded from theme.liquid on bed product pages only (product.type gate).
*/
(function () {
  'use strict';

  /* Markets are fully separated — Americans only ever see .com in USD and
     Canadians only ever see .ca in CAD — so the market alone picks the
     default and there is nothing to resolve from a destination. */
  var RATE_BY_MARKET = { US: '$379', CA: '$399' };

  /* Every line closes off a way the price could still move. Verified against a
     year of US orders: tax is $0 and duties are null on all of them, so the
     total a shopper sees really is subtotal plus this rate. */
  var US_POINTS = ['Complimentary shipping insurance',
                   'Duties, taxes and fees included',
                   'No tailgate or residential surcharge',
                   'One flat fee, however many pieces'];

  /* Canada must NOT inherit the list above: GST/PST/HST is added at Canadian
     checkout, so "duties, taxes and fees included" would be a falsehood. */
  var CA_POINTS = ['Complimentary shipping insurance',
                   'Same flat rate to every province',
                   'One flat fee, however many pieces'];

  /* Alaska and Hawaii are quoted lanes, not this offer, so only the two
     claims that hold everywhere are made. */
  var SAFE_POINTS = ['Quoted before you buy', 'Fully insured door to door'];

  /* Named exactly as the delivery profile names it, so the product page, the
     cart and the checkout line all agree. Courtney, 2026-09-19. */
  var SERVICE_NAME = 'Unlimited Furniture Delivery';

  var SPEED_LABEL = 'Manufacturing Speed';

  var QUALITY_NOTE = 'Artisan crafted to order in Canada + the USA';
  var QUALITY_LEAD_DEFAULT = '3-year warranty on frame, stitching and suspension';
  var QUALITY_LEAD_BY_TYPE = {
    'Electric Salon Bed': '3-year warranty on frame and seams, 2-year on motion parts'
  };

  var TRUST_FIND    = 'freight calculated at checkout';
  var TRUST_REPLACE = 'Ships fully insured, door to door';

  var BLOCK_CLASS = 'po-freight';
  var STYLE_ID    = 'po-freight-note-style';

  function tag() {
    return document.querySelector('script[data-po-product-type]');
  }

  function attr(name) {
    var el = tag();
    return el ? (el.getAttribute(name) || '') : '';
  }

  function qualityLead() {
    return QUALITY_LEAD_BY_TYPE[attr('data-po-product-type')] || QUALITY_LEAD_DEFAULT;
  }

  function market() {
    var iso = '';
    try { iso = (window.Shopify && window.Shopify.country) || ''; } catch (e) {}
    if (!iso) {
      try {
        var cur = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
        if (cur === 'CAD') iso = 'CA';
      } catch (e) {}
    }
    return String(iso).toUpperCase();
  }

  function isCanada() { return market() === 'CA'; }

  /* An unrecognised market shows the structure without a figure rather than
     defaulting to the US number — a missing price reads as an oversight, a
     wrong price is a chargeback. */
  function defaultRate() { return RATE_BY_MARKET[market()] || ''; }

  var ICON_BOX =
    '<svg class="po-freight__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M21 8.2 12 3 3 8.2v7.6L12 21l9-5.2V8.2Z"/><path d="m3 8.2 9 5.2 9-5.2"/>' +
    '<path d="M12 13.4V21"/></svg>';

  var ICON_SHIELD =
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" ' +
    'stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M8 1.4 2.6 3.6v4.1c0 3 2.2 5.4 5.4 6.9 3.2-1.5 5.4-3.9 5.4-6.9V3.6L8 1.4Z"/>' +
    '<path d="M5.7 7.9 7.3 9.5l3-3.2"/></svg>';

  var ICON_TICK =
    '<svg class="po-freight__tick" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
    '<circle cx="8" cy="8" r="7" fill="currentColor" opacity=".13"/>' +
    '<path d="M4.9 8.2 6.9 10.2 11.1 5.9" stroke="currentColor" stroke-width="1.7" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var CSS =
    /* The build card keeps the taupe ground it already had; delivery and
       payment become their own white cards so the three decisions read as
       three steps rather than one crowded panel. */
    '.po-freight{margin:14px 0 0;padding:15px 16px;border-radius:10px;background:#fff;' +
      'border:1px solid rgba(0,0,0,.09);box-shadow:0 1px 2px rgba(0,0,0,.05);}' +
    '.po-freight__head{display:flex;align-items:flex-start;gap:11px;}' +
    '.po-freight__icon{flex:0 0 auto;width:21px;height:21px;margin-top:1px;opacity:.8;}' +
    '.po-freight__copy{min-width:0;}' +
    '.po-freight__rate{display:block;font-weight:600;font-size:1.15em;line-height:1.25;}' +
    '.po-freight__svc{display:block;margin-top:3px;font-size:.85em;opacity:.58;}' +
    /* The destination line reads as answered, with the change offered beside
       it — never as an empty field waiting to be filled. */
    '.po-freight__dest{display:flex;flex-wrap:wrap;align-items:baseline;gap:6px;' +
      'margin:10px 0 0;font-size:.85em;}' +
    '.po-freight__dest-label{opacity:.58;}' +
    '.po-freight__dest-value{font-weight:500;}' +
    '.po-freight__change{background:none;border:0;padding:0;font:inherit;cursor:pointer;' +
      'text-decoration:underline;text-underline-offset:2px;opacity:.75;}' +
    '.po-freight__change:hover{opacity:1;}' +
    '.po-freight__list{list-style:none;margin:13px 0 0;padding:0;display:grid;gap:7px;}' +
    '.po-freight__list li{display:flex;align-items:center;gap:9px;font-size:.9em;line-height:1.3;}' +
    '.po-freight__tick{flex:0 0 auto;width:15px;height:15px;}' +
    '.po-freight__note{display:block;margin:11px 0 0;font-size:.82em;line-height:1.4;opacity:.55;}' +
    /* The estimator is the configurator's own markup, relocated into this
       card. It keeps its own styles; only the reveal is ours. */
    '.po-freight__est{margin:11px 0 0;}' +
    '.po-freight__est[hidden]{display:none;}';

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  /* Free shipping was switched off entirely on 2026-09-19, so this line is not
     merely discouraging any more — "Spend $7,402 more to qualify for free
     shipping" is now false. */
  function hideDeficit() {
    var quiet = document.querySelector('[data-poc-freeship-offer]') ||
                document.querySelector('.poc-offer--quiet');
    if (quiet && quiet.style.display !== 'none') quiet.style.display = 'none';
  }

  function val(selector) {
    var el = document.querySelector(selector);
    return el ? (el.value || '') : '';
  }

  /* The estimator's own result line is the source of truth once it has run.
     Anything without a currency figure is a prompt or an error, not a quote. */
  function parseResult() {
    var el = document.querySelector('[data-poc-est-result]') ||
             document.querySelector('.poc-estimate__result');
    if (!el) return null;
    var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text) return null;
    var money = text.match(/\$[\d,]+(?:\.\d{2})?/);
    if (!money) return null;
    return {
      text: text,
      amount: money[0],
      service: text.split('—')[0].replace(/[\s–—-]+$/, '').trim()
    };
  }

  function serviceKind(service) {
    if (!/unlimited/i.test(service)) return 'other';
    return /canada/i.test(service) ? 'ca' : 'us';
  }

  /* ⚠ The estimator's state dropdown carries a value before anything is
     chosen — it defaulted to Alabama, and this panel dutifully printed
     "Shipping to Alabama" to every visitor. A destination is only real once
     the estimator has actually returned a quote for it, so the province is
     used ONLY alongside a quote; otherwise the market's own default is named. */
  function destinationLabel() {
    var quoted = parseResult();
    if (quoted) {
      var prov = val('select[data-poc-est-province]') || val('.poc-estimate__province');
      var zip = (val('[data-poc-est-zip]') || val('.poc-estimate__zip')).trim();
      if (prov && zip) return prov + ' ' + zip;
      if (prov) return prov;
    }
    return isCanada() ? 'Canada' : 'Contiguous United States';
  }

  /* The panel belongs between the build card and the payment choice. The form
     holds the deposit options and Add to Cart, so inserting before it puts
     delivery ahead of both — which is the whole point of the reorder.

     ⚠ The page carries MORE THAN ONE cart form: the visible one in the
     summary, and at least one inside a display:none ancestor. Selecting the
     first match put the panel inside the hidden one, where it rendered
     correctly and measured 0px tall. Scope to the summary and take a form
     that is actually laid out. */
  function visibleProductForm() {
    var summary = document.querySelector('.poc-summary');
    if (!summary) return null;
    var forms = summary.querySelectorAll('form.shopify-product-form, form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].getClientRects().length) return forms[i];
    }
    return null;
  }

  function placeBeforeForm(wrap) {
    var form = visibleProductForm();
    if (!form || !form.parentNode) return;
    if (wrap.nextElementSibling === form) return;
    form.parentNode.insertBefore(wrap, form);
  }

  function renderBlock() {
    var summary = document.querySelector('.poc-summary');
    if (!summary) return;

    var quote = parseResult();
    var ca = isCanada();

    var kind = quote ? serviceKind(quote.service) : (ca ? 'ca' : 'us');
    var points = kind === 'us' ? US_POINTS : kind === 'ca' ? CA_POINTS : SAFE_POINTS;

    var amount = quote ? quote.amount : defaultRate();
    var free = /^\$0(?:\.00)?$/.test(amount);

    /* Crate & Barrel head their block with the SERVICE — "In-Home Delivery" —
       and put the terms under it. We lead with the PRICE and the service
       together, because unlike theirs ours is a single number that covers the
       whole order, and that number is the argument. Courtney, 2026-09-19:
       "I want the shipping rate in the Bold Title... I dont want the price
       small." So the bold line reads "$379 Unlimited Furniture Delivery" and
       the terms sit under it, with no figure competing from the sub-line. */
    var lead = amount ? amount + ' ' + SERVICE_NAME : SERVICE_NAME;
    var sub;
    if (free) {
      lead = 'Delivery included';
      sub = 'No surprises at checkout.';
    } else if (kind === 'us') {
      sub = 'One fee covers your whole order, however many pieces.';
    } else if (kind === 'ca') {
      sub = 'One fee covers your whole order, to every province.';
    } else {
      /* Alaska, Hawaii and any hand-quoted lane. The estimator has returned a
         real figure for a real destination, so it is named as that lane's own
         service rather than dressed as the flat offer, which it is not. */
      lead = amount + ' ' + (quote && quote.service ? quote.service : 'Delivery');
      sub = 'Quoted for ' + destinationLabel() + ', fully insured.';
    }

    var wrap = document.querySelector('.po-freight-wrap');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'po-freight-wrap';
      wrap.innerHTML =
        '<div class="' + BLOCK_CLASS + '">' +
        '<div class="po-freight__head">' + ICON_BOX +
        '<span class="po-freight__copy"><span class="po-freight__rate"></span>' +
        '<span class="po-freight__svc"></span></span></div>' +
        '<div class="po-freight__dest">' +
        '<span class="po-freight__dest-label">Shipping to</span>' +
        '<span class="po-freight__dest-value"></span>' +
        '<button type="button" class="po-freight__change">Change</button></div>' +
        '<div class="po-freight__est" hidden></div>' +
        '<ul class="po-freight__list"></ul>' +
        '<span class="po-freight__note"></span></div>';
      summary.appendChild(wrap);

      wrap.querySelector('.po-freight__change').addEventListener('click', function () {
        var slot = wrap.querySelector('.po-freight__est');
        slot.hidden = !slot.hidden;
        this.textContent = slot.hidden ? 'Change' : 'Done';
      });
    }

    placeBeforeForm(wrap);

    /* The estimator is moved into the card rather than duplicated, so it stays
       the same element the configurator already wires up. */
    var est = document.querySelector('.poc-estimate');
    var slot = wrap.querySelector('.po-freight__est');
    if (est && est.parentNode !== slot) {
      est.style.display = '';
      slot.appendChild(est);
    }

    var card   = wrap.querySelector('.' + BLOCK_CLASS);
    var rateEl = card.querySelector('.po-freight__rate');
    var svcEl  = card.querySelector('.po-freight__svc');
    var destEl = card.querySelector('.po-freight__dest-value');
    var listEl = card.querySelector('.po-freight__list');
    var noteEl = card.querySelector('.po-freight__note');

    /* The Alaska/Hawaii caveat used to sit here permanently. Courtney,
       2026-09-19: "The only place that should be displayed is if they change
       their zip to Hawaii or Alaska. It should change the price to their
       rates." It does — the estimator quotes those lanes for real — so the
       standing note was warning the 99% about the 1%. Canada keeps its tax
       line, which is a statement of fact rather than a caveat. */
    var note = ca ? 'Taxes are shown at checkout.' : '';

    /* Compare against what is rendered, never a marker attribute — the
       configurator rewrites this region and a flag would go stale. */
    if (rateEl.textContent !== lead) rateEl.textContent = lead;
    if (svcEl.textContent !== sub) svcEl.textContent = sub;
    if (destEl.textContent !== destinationLabel()) destEl.textContent = destinationLabel();
    if (noteEl.textContent !== note) noteEl.textContent = note;

    var current = [];
    for (var i = 0; i < listEl.children.length; i++) {
      current.push(listEl.children[i].textContent);
    }
    if (current.join('|') !== points.join('|')) {
      listEl.textContent = '';
      for (var j = 0; j < points.length; j++) {
        var li = document.createElement('li');
        li.innerHTML = ICON_TICK;
        var span = document.createElement('span');
        span.textContent = points[j];
        li.appendChild(span);
        listEl.appendChild(li);
      }
    }
  }

  function applyTrust() {
    var items = document.querySelectorAll('.poc-trust li span');
    for (var i = 0; i < items.length; i++) {
      var text = (items[i].textContent || '').replace(/\s+/g, ' ');
      if (text === TRUST_REPLACE) continue;
      if (text.indexOf(TRUST_FIND) === -1) continue;
      items[i].textContent = TRUST_REPLACE;
    }
  }

  /* The build window is already stated in the summary rows, so the shipnote
     block below the button was a third telling of the same fact. The block is
     cloned so the warranty note is pixel-identical by construction, its data
     hooks stripped so the configurator stops rewriting it back to dates. */
  function buildQualityNote() {
    var shipnote = document.querySelector('[data-poc-shipnote]');
    if (!shipnote) return;

    var existing = document.querySelector('.po-quality');
    if (!existing) {
      var clone = shipnote.cloneNode(true);
      clone.removeAttribute('data-poc-shipnote');
      clone.removeAttribute('style');
      clone.className = 'poc-shipnote po-quality';

      var icon = clone.querySelector('.poc-shipnote__icon');
      if (icon) icon.innerHTML = ICON_SHIELD;

      var lead = clone.querySelector('[data-poc-shipwindow]');
      if (lead) {
        lead.removeAttribute('data-poc-shipwindow');
        lead.textContent = qualityLead();
      }

      var note = clone.querySelector('.poc-shipnote__note');
      if (note) {
        note.removeAttribute('data-poc-shiptransit');
        note.textContent = QUALITY_NOTE;
      }

      shipnote.parentNode.insertBefore(clone, shipnote);
      existing = clone;
    }

    if (shipnote.style.display !== 'none') shipnote.style.display = 'none';

    var trust = document.querySelector('.poc-trust');
    if (trust && trust.style.display !== 'none') trust.style.display = 'none';
  }

  /* "Ships in — 6–8 weeks" states a duration without saying it is a choice, so
     the Rush upgrade reads as unrelated. Naming the row for the decision and
     the value for the option taken makes the two obviously the same lever. */
  function relabelSpeed() {
    var rows = document.querySelectorAll('[data-poc-spec-rows] .poc-summary__row');
    var rush = document.querySelector('[data-poc-rush]');
    var isRush = !!(rush && rush.checked);

    for (var i = 0; i < rows.length; i++) {
      var dt = rows[i].querySelector('dt');
      var dd = rows[i].querySelector('dd');
      if (!dt || !dd) continue;

      var label = (dt.textContent || '').replace(/\s+/g, ' ').trim();
      if (label !== 'Ships in' && label !== SPEED_LABEL) continue;

      var raw = (dd.textContent || '').replace(/\s+/g, ' ').trim();
      var wrapped = raw.match(/\(([^)]*)\)\s*$/);
      var weeks = wrapped ? wrapped[1] : raw;
      var want = (isRush ? 'Rush (' : 'Standard (') + weeks + ')';

      if (dt.textContent !== SPEED_LABEL) dt.textContent = SPEED_LABEL;
      if (dd.textContent !== want) dd.textContent = want;
    }
  }

  function apply() {
    injectStyle();
    hideDeficit();
    applyTrust();
    buildQualityNote();
    relabelSpeed();
    renderBlock();
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; apply(); });
  }

  function start() {
    apply();
    var scope = document.querySelector('.poc-summary') || document.body;
    try {
      new MutationObserver(schedule).observe(scope, { childList: true, subtree: true, characterData: true });
    } catch (e) {}
    /* Choosing a state mutates no attribute, so the observer never sees it,
       and the events alone left the panel a step behind whatever fired first.
       Both are handled for immediacy, then a slow poll guarantees the panel is
       correct however the value got set. apply() exits early when nothing has
       changed, so the poll costs a few selector lookups. */
    document.addEventListener('change', schedule, true);
    document.addEventListener('input', schedule, true);
    setInterval(apply, 400);
    var delays = [300, 800, 1600, 2600];
    for (var i = 0; i < delays.length; i++) setTimeout(apply, delays[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();